import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import type { OntologyInstance, OntologyDomain } from '@/types/ontology'
import { getLocalName } from '@/lib/ontology/namespaces'
import { Circle, Loader2 } from 'lucide-react'

interface InstanceListProps {
  instances: OntologyInstance[]
  selectedInstance: OntologyInstance | null
  onSelectInstance: (instance: OntologyInstance) => void
  loading?: boolean
  error?: string | null
}

const domainColors: Record<OntologyDomain, string> = {
  core: 'text-amber-600',
  market: 'text-market',
  business: 'text-business',
  solution: 'text-solution',
}

export function InstanceList({
  instances,
  selectedInstance,
  onSelectInstance,
  loading,
  error,
}: InstanceListProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-text-muted">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading instances...
      </div>
    )
  }

  if (error) {
    return (
      <div className="py-8 text-center text-solution">
        <p className="font-medium">Error loading instances</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    )
  }

  if (instances.length === 0) {
    return (
      <div className="py-8 text-center text-text-muted">
        <p>No instances found</p>
        <p className="text-sm mt-1">Create a new instance or select a different class</p>
      </div>
    )
  }

  // Group by type
  const grouped = instances.reduce(
    (acc, instance) => {
      const type = instance.typeLabel
      if (!acc[type]) {
        acc[type] = { domain: instance.domain, instances: [] }
      }
      acc[type].instances.push(instance)
      return acc
    },
    {} as Record<string, { domain: OntologyDomain; instances: OntologyInstance[] }>
  )

  return (
    <div className="space-y-3">
      {Object.entries(grouped).map(([type, { domain, instances: typeInstances }]) => (
        <div key={type} className="border border-border rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-background flex items-center gap-2">
            <Circle className={cn('w-2 h-2 fill-current', domainColors[domain])} />
            <span className="font-mono text-sm font-medium">{type}</span>
            <Badge variant="outline" className="ml-auto text-xs">
              {typeInstances.length}
            </Badge>
          </div>
          <ul className="divide-y divide-border">
            {typeInstances.map((instance) => (
              <li key={instance.uri}>
                <button
                  onClick={() => onSelectInstance(instance)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                    selectedInstance?.uri === instance.uri
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-background text-text'
                  )}
                >
                  <span className="truncate">{instance.label}</span>
                  <span className="ml-auto text-xs text-text-muted font-mono truncate max-w-[150px]">
                    {getLocalName(instance.uri)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}
